// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import React, {createContext, useContext, useMemo} from 'react';
import {use_dashboard_data} from './use_dashboard_data';
import {App_context, i_user_info} from 'hola_app_context';
import {show_free_plan_promo} from '../ui/dashboard/utils';
import {t_order} from '../../../hola/pub/ui/pro/cp/orders';
import {get_renew_error_orders} from '../ui/dashboard/utils';
import {use_kyc} from './use_kyc';
import {use_account_data} from './use_account_data';

interface i_app_context {
    dashboard: ReturnType<typeof use_dashboard_data>;
    kyc: ReturnType<typeof use_kyc>;
    account: ReturnType<typeof use_account_data>;
    show_free_zone_promo: boolean;
    show_free_group_promo: boolean;
    renew_error_orders: t_order[];
    renew_error_expired_in_day: boolean;
    renew_error_expired_in_two_days: boolean;
    user: i_user_info;
    has_user: boolean;
    is_client: boolean;
    is_plus: boolean;
}

const AppContext = createContext<i_app_context|undefined>(undefined);

export const use_app_context = ()=>{
    const context = useContext(AppContext);
    if (!context)
    {
        throw new Error(
            'use_app_context must be used with AppContextProvider');
    }
    return context;
};

interface i_app_context_provider {
    children: React.ReactNode;
    disable_kyc?: boolean;
    use_account_data?: boolean;
    use_sessions?: boolean;
}
export const AppContextProvider = (params: i_app_context_provider)=>{
    const {user, is_client, is_plus} = useContext(App_context);
    const dashboard = use_dashboard_data(user, params.use_sessions);
    const account = use_account_data(user, params.use_account_data);
    const kyc = use_kyc(user, params.disable_kyc);
    const {info} = dashboard ?? {};
    const show_free_zone_promo = useMemo(()=>{
        if (!info?.inited)
          return false;
        return show_free_plan_promo(info?.orders, info?.free_zone_params);
    }, [user, info?.orders, info?.inited, info?.free_zone_params]);
    const show_free_group_promo = useMemo(()=>{
        if (!info?.inited)
          return false;
        return (info?.zone_groups ?? []).length == 0;
    }, [user, info?.zone_groups, info?.inited]);
    const renew_error_orders = useMemo(()=>{
        return info?.inited ? get_renew_error_orders(info?.orders) : [];
    }, [info?.orders, info?.inited]);
    const {renew_error_expired_in_day,
        renew_error_expired_in_two_days} = useMemo(()=>{
        return {
            renew_error_expired_in_day:
                renew_error_orders.some(o=>o.blocking_in_hours <= 24),
            renew_error_expired_in_two_days:
                renew_error_orders.some(o=>o.blocking_in_hours <= 48),
        };
    }, [renew_error_orders]);
    return (
      <AppContext.Provider
        value={{
          dashboard, show_free_zone_promo, show_free_group_promo,
          user, has_user: !!user, is_client, is_plus,
          renew_error_orders, renew_error_expired_in_day,
          renew_error_expired_in_two_days, kyc, account,
        }}>
        {params.children}
      </AppContext.Provider>
    );
};

